import Button from "@ui/button/Button"
import { EntityInput } from "./EntityInput"
import { useEffect, useMemo, useRef, useState } from "react"
import { toast } from "react-toastify"
import { systemErrorMessage, systemPost } from "src/services/superAdminApi"
import { formatMinorCurrency } from "src/utils/systemBalance"

import { T } from "../adminTheme"
import { Avatar, Callout, date, Disclosure, Fact, Facts, Fields, Muted, Page, Paged, Panel, Row, State, StatusBadge, Table, Tabs } from "./adminUi"
import { UserOverviewData } from "./UserOverviewData"
import { CardholderFlow, type ICardholderFlow } from "./CardholderFlow"
import { eventLabel } from "./SuperAdminEvents"
import { UserAccessActions } from "./UserAccessActions"
import { UserCompanyMove } from "./UserCompanyMove"
import { UserEmailChange } from "./UserEmailChange"

interface IUserFlow {
	login_enabled?: boolean
	email_confirmed?: boolean
	our_kyc_status?: string
	approved_holders?: number
	approved_issuers?: string[]
	can_issue_cards?: boolean
	blocking?: string[]
	next_action?: string
	email_required?: boolean
	our_kyc_required?: boolean
	cards_scope?: "own" | "none"
	cards_note?: string | null
}


export interface ISystemUserOverview {
	user?: Record<string, unknown>
	company?: Record<string, unknown> | null
	issuers?: Array<Record<string, unknown>>
	teams?: Array<Record<string, unknown>>
	balances?: {
		buyer?: unknown
		company?: unknown
	}
	cardholders?: Array<Record<string, unknown> & { flow?: ICardholderFlow }>
	cards?: {
		total?: number
		by_status?: Record<string, number>
		balance_cents?: number
		balance_source?: "issuer" | "db_mirror" | string
		currency?: string
		rows?: unknown[]
		list?: unknown[]
		[key: string]: unknown
	}
	kyc?: unknown
	events?: { total?: number; rows?: unknown[] } | unknown[]
	block_operations?: unknown[]
	flow?: IUserFlow
	origin?: unknown
	sessions?: unknown
	transactions?: unknown
}

const DetailGroup = ({ label, value }: { label: string; value: unknown }) => <Disclosure label={label}><UserOverviewData value={value} /></Disclosure>

const FlowBanner = ({ flow }: { flow?: IUserFlow }) => {
	const blocking = (flow?.blocking ?? []).filter((item) => item !== flow?.next_action)
	const noCards = flow?.cards_scope === "none"
	const ready = (Boolean(flow?.can_issue_cards) || noCards) && (flow?.blocking ?? []).length === 0
	// Without a verdict or a next step the banner only adds noise.
	if (!flow || (!ready && !flow.next_action && blocking.length === 0)) return null
	return <Callout role='status' tone={ready ? "success" : "warn"} title={ready ? (noCards ? "Nothing is blocking this user" : "Ready to issue cards") : "User flow needs attention"}>
		{noCards && flow.cards_note ? <div>{flow.cards_note}</div> : flow.next_action && <div>{flow.next_action}</div>}
		{blocking.length > 0 && <ul>{blocking.map((item, index) => <li key={index}>{item}</li>)}</ul>}
	</Callout>
}

const Sub = ({ children }: { children: React.ReactNode }) => <small style={{ display: "block", color: T.muted, fontWeight: 400, fontSize: 12.5, overflowWrap: "anywhere" }}>{children}</small>
const status = (value: unknown) => <StatusBadge value={value ? String(value) : undefined} />

export const SuperAdminUserOverview = ({ token, initialUserUid = "", embedded = false, onShowAllEvents }: { token: string; initialUserUid?: string; embedded?: boolean; onShowAllEvents?: (uid: string) => void }) => {
	const [userUid, setUserUid] = useState(initialUserUid)
	const [report, setReport] = useState<ISystemUserOverview | null>(null)
	const [loading, setLoading] = useState(false)
	const [section, setSection] = useState<"overview" | "user" | "company" | "holders" | "events" | "diagnostics">("overview")
	const requestId = useRef(0)

	useEffect(() => {
		const id = ++requestId.current
		setUserUid(initialUserUid)
		setReport(null)
		setLoading(false)
		if (!initialUserUid.trim()) return () => { requestId.current++ }
		let active = true
		setLoading(true)
		void systemPost<ISystemUserOverview>(token, "system/user/overview", { user_uid: initialUserUid.trim(), refresh: false }).then((response) => {
			if (!active || id !== requestId.current) return
			setLoading(false)
			if (response.error) toast.error(systemErrorMessage(response.error))
			else setReport(response.data)
		})
		return () => { active = false; requestId.current++ }
	}, [initialUserUid, token])

	const currency = String(report?.cards?.currency || (report?.cards?.rows as Array<{ currency?: string }> | undefined)?.[0]?.currency || "USD")
	const cardStatusSummary = useMemo(() => Object.entries(report?.cards?.by_status ?? {}), [report?.cards?.by_status])
	const eventRows = Array.isArray(report?.events) ? report.events : report?.events?.rows ?? []
	const eventTotal = Array.isArray(report?.events) ? eventRows.length : report?.events?.total ?? eventRows.length
	const overviewEvents = eventRows.filter((event): event is Record<string, unknown> => event !== null && typeof event === "object" && !Array.isArray(event))
	const ourKycStatus = report?.kyc && typeof report.kyc === "object" && "our_status" in report.kyc ? String(report.kyc.our_status) : undefined

	const load = async (refresh: boolean) => {
		if (!userUid.trim()) return toast.warning("Enter a user UID")
		const id = ++requestId.current
		setLoading(true)
		setReport(null)
		const response = await systemPost<ISystemUserOverview>(token, "system/user/overview", { user_uid: userUid.trim(), refresh })
		if (id !== requestId.current) return
		setLoading(false)
		if (response.error) return toast.error(systemErrorMessage(response.error))
		setReport(response.data)
	}

	/** After a manual approval action: re-read the card in place, without clearing it. */
	const reload = async () => {
		const id = ++requestId.current
		const response = await systemPost<ISystemUserOverview>(token, "system/user/overview", { user_uid: String(report?.user?.uid ?? userUid).trim(), refresh: false })
		if (id !== requestId.current) return
		if (response.error) toast.error(systemErrorMessage(response.error))
		else setReport(response.data)
	}

	const tabs = [
		["overview", "Overview"], ["user", "User"], ["company", "Company"],
		["holders", `Cardholders (${report?.cardholders?.length ?? 0})`],
		["events", `Events (${eventTotal})`],
		["diagnostics", "Diagnostics"]
	] as const
	const refresh = <Button variant='secondarySmall' disabled={loading || !userUid.trim()} onClick={() => void load(true)}>{loading ? "Loading…" : "Refresh issuer balances"}</Button>
	return <Page>
		{embedded
			? <Row style={{ justifyContent: "space-between", gap: 12 }}>
				<Row style={{ gap: 12, flexWrap: "nowrap", minWidth: 0 }}>
					<Avatar name={String(report?.user?.name || report?.user?.email || userUid)} size={40} />
					<div style={{ minWidth: 0 }}><h3 style={{ fontSize: 17 }}>{String(report?.user?.name || "User")}</h3><Muted style={{ fontSize: 13 }}>{report?.user?.email ? `${String(report.user.email)} · ` : ""}UID <code>{userUid}</code></Muted></div>
				</Row>
				{refresh}
			</Row>
			: <Panel title='User' hint='Find the user by name, e-mail or UID to see their account, KYC, card holders and recent events in one place.'>
				<Fields style={{ gridTemplateColumns: "minmax(220px, 420px)" }}>
					<label onKeyDown={(event) => { if (event.key === "Enter") void load(false) }}>User<EntityInput token={token} kind='user' name='userUid' value={userUid} onChange={(value) => { requestId.current++; setLoading(false); setUserUid(value); setReport(null) }} /></label>
				</Fields>
				<Row style={{ marginTop: 16 }}><Button variant='primarySmall' disabled={loading} onClick={() => void load(false)}>{loading ? "Loading…" : "Load overview"}</Button>{report && refresh}</Row>
			</Panel>}
		<State loading={loading && !report} />
		{report && <Panel>
			<Tabs role='tablist' aria-label='User overview sections'>
				{tabs.map(([id, label]) => <button key={id} type='button' role='tab' aria-selected={section === id} aria-controls={`overview-panel-${id}`} onClick={() => setSection(id)}>{label}</button>)}
			</Tabs>
			<div id={`overview-panel-${section}`} role='tabpanel' aria-label={tabs.find(([id]) => id === section)?.[1]} style={{ display: "grid", gap: 14, minWidth: 0 }}>
				{section === "overview" && <>
					<FlowBanner flow={report.flow} />
					<UserAccessActions token={token} flow={report.flow} user={report.user} company={report.company} kyc={report.kyc} onChanged={reload} />
					<Facts>
						<Fact label='User KYC'>{status(ourKycStatus)}</Fact>
						<Fact label='Cards'>{report.cards?.total ?? 0}<Sub>{cardStatusSummary.map(([name, count]) => `${name.replaceAll("_", " ")}: ${count}`).join(" · ") || "No status summary"}</Sub></Fact>
						<Fact label='Card balance'>{formatMinorCurrency(report.cards?.balance_cents, currency)}<Sub>{`Source: ${String(report.cards?.balance_source || "unknown").replaceAll("_", " ")}`}</Sub></Fact>
						<Fact label='Approved holders'>{report.flow?.approved_holders ?? 0}<Sub>{report.flow?.approved_issuers?.join(", ") || "No approved issuers"}</Sub></Fact>
						<Fact label='User'>{String(report.user?.name || "—")}<Sub>{String(report.user?.email || "—")}</Sub></Fact>
						<Fact label='User status'>{status(report.user?.status)}</Fact>
						<Fact label='Company'>{String(report.company?.name || "—")}<Sub>{String(report.company?.type || "—")}</Sub></Fact>
						<Fact label='Company status'>{status(report.company?.status)}</Fact>
					</Facts>
					{typeof report.user?.uid === "string" && typeof report.user?.email === "string" && <UserEmailChange token={token} userUid={report.user.uid} email={report.user.email} onChanged={reload} />}
					{typeof report.user?.uid === "string" && <UserCompanyMove token={token} userUid={report.user.uid} userLabel={String(report.user.name || report.user.email || report.user.uid)} companyName={String(report.company?.name || "")} onChanged={reload} />}
				</>}
				{section === "user" && <UserOverviewData value={report.user ?? null} />}
				{section === "company" && <UserOverviewData value={report.company ?? null} />}
				{section === "holders" && (!report.cardholders?.length ? <State empty emptyText='No cardholders.' /> : <Paged rows={report.cardholders}>{(pageRows) => <Table>
					<thead><tr><th>Issuer</th><th>Holder status</th><th>Flow</th></tr></thead>
					<tbody>{pageRows.map((holder, index) => <tr key={String(holder.uid ?? index)}>
						<td><span style={{ fontWeight: 600 }}>{String(holder.provider_name || holder.provider_code || "Cardholder")}</span><small>{String(holder.type || holder.uid || "")}</small></td>
						<td>{status(holder.status)}</td>
						<td>
							<CardholderFlow flow={holder.flow} ourKycStatus={ourKycStatus} />
							<div style={{ marginTop: 8 }}><Disclosure flat label={<span style={{ color: T.accentText }}>All cardholder fields</span>}><UserOverviewData value={holder} /></Disclosure></div>
						</td>
					</tr>)}</tbody>
				</Table>}</Paged>)}
				{section === "events" && <>
					{onShowAllEvents && <Row><Button variant='secondarySmall' onClick={() => onShowAllEvents(userUid)}>Open full event journal</Button></Row>}
					{overviewEvents.length === 0 ? <State empty emptyText='No events for this user.' /> : <Paged rows={overviewEvents}>{(pageRows) => <Table>
						<thead><tr><th>Event</th><th>Result</th><th>When</th></tr></thead>
						<tbody>{pageRows.map((event, index) => <tr key={String(event.uid ?? index)}>
							<td><span style={{ fontWeight: 600 }}>{eventLabel(String(event.action ?? event.type ?? "Event"))}</span>{event.message ? <small>{String(event.message)}</small> : null}
								<div style={{ marginTop: 6 }}><Disclosure flat label='Details'><UserOverviewData value={event} /></Disclosure></div></td>
							<td>{status(event.result)}</td>
							<td>{date(event.created_at ? String(event.created_at) : undefined)}</td>
						</tr>)}</tbody>
					</Table>}</Paged>}
				</>}
				{section === "diagnostics" && <>
					<DetailGroup label='Buyer balances by issuer' value={report.balances?.buyer ?? null} />
					<DetailGroup label='Company balances by issuer' value={report.balances?.company ?? null} />
					<DetailGroup label={`Issuers (${report.issuers?.length ?? 0})`} value={report.issuers ?? []} />
					<DetailGroup label={`Teams (${report.teams?.length ?? 0})`} value={report.teams ?? []} />
					<DetailGroup label='Cards' value={report.cards ?? null} />
					<DetailGroup label='KYC diagnostics' value={report.kyc ?? null} />
					<DetailGroup label={`Block operations (${report.block_operations?.length ?? 0})`} value={report.block_operations ?? []} />
					<DetailGroup label='Origin, sessions and events' value={{ origin: report.origin, sessions: report.sessions, events: report.events, transactions: report.transactions }} />
					<DetailGroup label='Full record' value={report} />
				</>}
			</div>
		</Panel>}
	</Page>
}
